import { create } from "zustand";
import { onIdTokenChanged, type User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../net/firebase";
import { PLAYERS } from "./playerDoc";
import { clearHostEntitlement, refreshHostEntitlement } from "../theme/entitlements";
import { setSignedInWithGoogle } from "../theme/events";

/**
 * Who the player is signed in as, and how their cloud save is getting on —
 * `docs/CLOUD_SAVES_DESIGN.md` §6.
 *
 * This store only *reflects* Firebase; it never drives it. Signing in and out
 * happens in `net/firebase.ts` and the username lives in Firestore, so both
 * arrive here through listeners. The UI reads this store, calls those modules,
 * and waits for the change to come back round — which means the account screen
 * shows the same thing after a sign-in on another tab as it does after one of
 * its own.
 */
export type AccountStatus = "loading" | "signedOut" | "anonymous" | "signedIn";

/** What the sync engine (`stats/cloudSync.ts`, §5) is currently doing. */
export type SyncState = "idle" | "syncing" | "readOnly" | "error";

/**
 * A first-sign-in collision (§B7): both this device and the account already
 * hold real progress, so the player has to say what to do with it. Raised and
 * shaped by the sync engine — the store only carries it to the UI.
 */
export interface PendingConflict {
  cloudSummary?: unknown;
}

interface AccountStoreState {
  status: AccountStatus;
  uid: string | null;
  email: string | null;
  provider: string | null;
  /**
   * The claimed username, or null when there isn't one. A `signedIn` player
   * with `username === null` is the "needs to claim a name" state, and it is
   * the same state whether the players doc is missing or hasn't loaded yet —
   * the account screen keys off it directly.
   */
  username: string | null;
  syncState: SyncState;
  pendingConflict: PendingConflict | null;

  setSyncState: (syncState: SyncState) => void;
  setPendingConflict: (conflict: PendingConflict | null) => void;
  /** The player has answered the conflict prompt; the sync engine carries out the choice. */
  resolveConflict: () => void;
  /** Internal: fed by `initAccountStore`'s listeners. Not for the UI to call. */
  _applyAuth: (user: User | null) => void;
  /** Internal: fed by the `players/{uid}` snapshot; undefined means no doc yet. */
  _applyPlayerDoc: (data: { displayName?: string } | undefined) => void;
}

export const useAccountStore = create<AccountStoreState>((set) => ({
  status: "loading",
  uid: null,
  email: null,
  provider: null,
  username: null,
  syncState: "idle",
  pendingConflict: null,

  setSyncState(syncState) {
    set({ syncState });
  },

  setPendingConflict(pendingConflict) {
    set({ pendingConflict });
  },

  resolveConflict() {
    set({ pendingConflict: null });
  },

  _applyAuth(user) {
    if (!user) {
      set({
        status: "signedOut",
        uid: null,
        email: null,
        provider: null,
        username: null,
        syncState: "idle",
        pendingConflict: null,
      });
      return;
    }
    set((s) => {
      const sameAccount = user.uid === s.uid;
      return {
        status: user.isAnonymous ? "anonymous" : "signedIn",
        uid: user.uid,
        email: user.email ?? null,
        provider: user.providerData[0]?.providerId ?? null,
        // A different uid starts blank and waits for its own snapshot. Carrying
        // the previous account's username over would flash their name on the
        // account screen of whoever just signed in. The same uid keeps it,
        // because linking an anonymous session promotes the uid in place and
        // there is no reason to blink.
        username: sameAccount ? s.username : null,
        syncState: sameAccount ? s.syncState : "idle",
        pendingConflict: sameAccount ? s.pendingConflict : null,
      };
    });
  },

  _applyPlayerDoc(data) {
    set({ username: data?.displayName ?? null });
  },
}));

let unsubscribeAuth: (() => void) | null = null;

/**
 * Points the store at Firebase. Called once at app start.
 *
 * Idempotent: a second call is ignored rather than stacking a second pair of
 * listeners, which would double every update and leak the first pair — the
 * usual way that happens is a hot reload, not a careless caller.
 */
export function initAccountStore(): void {
  if (unsubscribeAuth) return;

  let unsubscribePlayer: (() => void) | null = null;
  let watchedUid: string | null = null;

  // Not onAuthStateChanged: that only speaks when the uid changes, and a guest
  // linking Google keeps theirs, so the screen would stay signed out until a reload.
  unsubscribeAuth = onIdTokenChanged(auth, (user) => {
    useAccountStore.getState()._applyAuth(user);
    // Seasonal events reward a Google account (theme/events.ts). Told here, on
    // every auth change, so the round recorder can ask without importing this.
    setSignedInWithGoogle(!!user && !user.isAnonymous && user.providerData.some((p) => p.providerId === "google.com"));

    // Anonymous players have no players doc and no rule that would let them
    // read one, so only real accounts get a listener.
    const wantUid = user && !user.isAnonymous ? user.uid : null;
    // onIdTokenChanged also fires for token refreshes on an unchanged user;
    // re-subscribing on those would churn a listener every hour for nothing.
    if (wantUid === watchedUid) return;

    // Approved hosts get the premium decks comped (theme/entitlements.ts), and
    // whether this account is one is a question only the server can answer. Ask
    // on the account CHANGING, which is the same condition the players-doc
    // listener uses — an hourly token refresh is not news.
    if (wantUid) void refreshHostEntitlement();
    else clearHostEntitlement();

    unsubscribePlayer?.();
    unsubscribePlayer = null;
    watchedUid = wantUid;
    if (!wantUid) return;

    unsubscribePlayer = onSnapshot(
      doc(db, PLAYERS, wantUid),
      (snap) => useAccountStore.getState()._applyPlayerDoc(snap.data() as { displayName?: string } | undefined),
      (err) => {
        // A dead listener leaves `username` where it is; the claim form is the
        // honest fallback, and claiming re-reads authoritatively anyway.
        console.error("[fiveout] player document listener failed", err);
      },
    );
  });
}
